import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, ButtonGroup, Button as BsButton, Form, Spinner, Table } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title as ChartTitle, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState } from '@tanstack/react-table';
import countries from 'i18n-iso-countries';
import {
  useGetOverview,
  useGetPlayerStats,
  useGetTournamentStats,
  useGetMatchStats,
  useGetTourStats,
  useGetOrganizationDetails,
  useGetClassStats,
  useGetTicketStats,
  useGetMessageStats,
} from '../../generated/orval/admin';
import type { StatEntryResponseDto, TicketStatsResponseDto, OrganizationDetailsResponseDto } from '../../generated/orval/schemas';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, ChartLegend);

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/sv.json'));
countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
countries.registerLocale(require('i18n-iso-countries/langs/es.json'));

const prefix = 'adminStats-';

type Metric = 'players' | 'tournaments' | 'matches' | 'tours' | 'organizations' | 'classes' | 'tickets' | 'messages';

function countryName(code: string, lang: string): string {
  const short = lang.substring(0, 2).toLowerCase();
  const isoLang = short === 'se' ? 'sv' : short;
  return countries.getName(code, isoLang) || code;
}

// --- Build pivot from backend response ---

// Assign a sort priority to time-period-like strings. Lower = first.
function getTimePriority(s: string): number {
  const k = s.toLowerCase().replace(/\s+/g, '');
  // Relative periods
  if (k.includes('thisweek')) return 0;
  if (k.includes('lastweek')) return 1;
  if (k.includes('thismonth')) return 2;
  if (k === 'lastmonth') return 3;
  if (k.includes('last') && k.includes('3')) return 4;
  if (k.includes('last') && k.includes('6')) return 5;
  if (k.includes('last') && k.includes('12')) return 6;
  // Pure year (e.g. "2026") — sort descending, after relative periods
  const yearMatch = s.match(/^(\d{4})$/);
  if (yearMatch) return 10 + (3000 - parseInt(yearMatch[1], 10)); // 2026→984, 2025→985, after relative periods (0-6), descending
  return 9999;
}

function isTimePeriodValue(s: string): boolean {
  const k = s.toLowerCase().replace(/\s+/g, '');
  return !!s.match(/^\d{4}$/) || k.includes('week') || k.includes('month') || k.includes('last');
}

// Sort values naturally
function naturalSort(a: string, b: string): number {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al === 'total') return 1;
  if (bl === 'total') return -1;
  if (al.startsWith('unspecified')) return 1;
  if (bl.startsWith('unspecified')) return -1;

  // If either looks like a time period, use time priority for both
  if (isTimePeriodValue(a) || isTimePeriodValue(b)) {
    return getTimePriority(a) - getTimePriority(b);
  }

  if (al.startsWith('under')) return bl.startsWith('under') ? a.localeCompare(b) : -1;
  if (bl.startsWith('under')) return 1;
  const numA = a.match(/^(\d+)/);
  const numB = b.match(/^(\d+)/);
  if (numA && numB) return parseInt(numA[1], 10) - parseInt(numB[1], 10);
  return a.localeCompare(b);
}

function buildTable(data: StatEntryResponseDto[]) {
  const rows = Array.from(new Set(data.map((r) => r.row)));
  const cols = Array.from(new Set(data.map((r) => r.column))).sort(naturalSort);
  const table: Record<string, Record<string, number>> = {};
  const rowTotals: Record<string, number> = {};

  for (const row of rows) { table[row] = {}; rowTotals[row] = 0; }
  for (const entry of data) {
    table[entry.row][entry.column] = (table[entry.row][entry.column] || 0) + entry.count;
    rowTotals[entry.row] += entry.count;
  }
  return { rows, cols, table, rowTotals };
}

// --- Reusable components ---

const StatCard = ({ label, value, subLabel, active, onClick }: {
  label: string; value: number; subLabel?: string; active?: boolean; onClick?: () => void;
}) => (
  <div
    className="text-center p-3 rounded"
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    style={{
      backgroundColor: active ? '#e8f0fe' : '#f8f9fa',
      border: active ? '2px solid #007AFF' : '1px solid #e5e5e5',
      minWidth: '140px',
      minHeight: '95px',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s, background-color 0.15s',
    }}
  >
    <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{value.toLocaleString()}</div>
    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{label}</div>
    {subLabel && <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#198754', marginTop: '4px' }}>{subLabel}</div>}
  </div>
);

const Loading = () => <div className="d-flex justify-content-center p-3"><Spinner animation="border" size="sm" /></div>;

const ErrorAlert = ({ prefix: p }: { prefix: string }) => {
  const { t } = useTranslation();
  return <Alert variant="danger">{t(p + 'fetchError')}</Alert>;
};

const StatTable = ({ data, rowLabel }: { data: StatEntryResponseDto[]; rowLabel: string }) => {
  const { t } = useTranslation();
  const { rows, cols, table, rowTotals } = useMemo(() => buildTable(data), [data]);
  const totalLabel = t(prefix + 'total');

  const tableData = useMemo(() => rows.map((row) => {
    const obj: Record<string, any> = { _row: row, _total: rowTotals[row] };
    for (const col of cols) obj[col] = table[row][col] || 0;
    return obj;
  }), [rows, cols, table, rowTotals]);

  const columns: ColumnDef<any>[] = useMemo(() => [
    {
      accessorKey: '_row',
      header: rowLabel,
      sortingFn: (a, b) => naturalSort(a.original._row, b.original._row),
    },
    ...cols.map((col): ColumnDef<any> => ({
      accessorKey: col,
      header: col,
      cell: (info) => (info.getValue<number>() || 0).toLocaleString(),
    })),
    {
      accessorKey: '_total',
      header: totalLabel,
      cell: (info) => <strong>{(info.getValue<number>() || 0).toLocaleString()}</strong>,
    },
  ], [cols, rowLabel, totalLabel]);

  const [sorting, setSorting] = useState<SortingState>([{ id: '_total', desc: true }]);

  const tt = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const colTotals = useMemo(() => cols.map((col) => rows.reduce((s, r) => s + (table[r][col] || 0), 0)), [cols, rows, table]);
  const grandTotal = useMemo(() => rows.reduce((s, r) => s + rowTotals[r], 0), [rows, rowTotals]);

  if (data.length === 0) return <Alert variant="info">{t(prefix + 'noData')}</Alert>;

  const cellPad = '10px 14px';
  const headerPad = '12px 14px';

  return (
    <div className="border rounded shadow-sm overflow-hidden">
      <Table hover responsive className="mb-0" style={{ fontSize: '0.9rem' }}>
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          {tt.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header, i) => {
                const sorted = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    role="button"
                    tabIndex={0}
                    className={i === 0 ? 'fw-semibold' : 'text-end fw-semibold'}
                    style={{ cursor: 'pointer', userSelect: 'none', padding: headerPad, borderBottom: '1px solid #dee2e6' }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span style={{ opacity: sorted ? 1 : 0.3, marginLeft: 4 }}>
                      {sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '↕'}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {tt.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell, i) => (
                <td key={cell.id} className={i === 0 ? '' : 'text-end'} style={{ padding: cellPad }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
          <tr className="fw-bold">
            <td style={{ padding: headerPad }}>{totalLabel}</td>
            {cols.map((col, i) => (
              <td key={col} className="text-end" style={{ padding: headerPad }}>
                {colTotals[i].toLocaleString()}
              </td>
            ))}
            <td className="text-end" style={{ padding: headerPad }}>{grandTotal.toLocaleString()}</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

type ViewMode = 'table' | 'chart';

const CHART_COLORS = ['#007AFF', '#198754', '#dc3545', '#fd7e14', '#6f42c1', '#20c997', '#0dcaf0', '#d63384'];

type ChartTimeMode = 'monthly' | 'yearly';

function isYearValue(s: string): boolean {
  return !!s.match(/^\d{4}$/);
}

// Matches backend "Mon YYYY" labels (e.g. "Jan 2026"). See statistics.service.ts:buildTimePeriodBuckets.
const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LABEL_REGEX = new RegExp(`^(${SHORT_MONTH_NAMES.join('|')}) \\d{4}$`);

function isMonthValue(s: string): boolean {
  return MONTH_LABEL_REGEX.test(s);
}

function compareYearLabel(a: string, b: string): number {
  return parseInt(a, 10) - parseInt(b, 10);
}

function compareMonthLabel(a: string, b: string): number {
  const [monthA, yearA] = a.split(' ');
  const [monthB, yearB] = b.split(' ');
  const yearDiff = parseInt(yearA, 10) - parseInt(yearB, 10);
  if (yearDiff !== 0) return yearDiff;
  return SHORT_MONTH_NAMES.indexOf(monthA) - SHORT_MONTH_NAMES.indexOf(monthB);
}

const StatChart = ({ data }: { data: StatEntryResponseDto[] }) => {
  const { t } = useTranslation();
  const [timeMode, setTimeMode] = useState<ChartTimeMode>('monthly');

  const { rows, cols, table } = useMemo(() => buildTable(data), [data]);

  const filteredRows = useMemo(() => {
    const filtered = rows.filter((r) => timeMode === 'yearly' ? isYearValue(r) : isMonthValue(r));
    // Chart X-axis: oldest→newest (left→right).
    return filtered.sort(timeMode === 'yearly' ? compareYearLabel : compareMonthLabel);
  }, [rows, timeMode]);

  const chartConfig = useMemo(() => ({
    labels: filteredRows,
    datasets: cols.map((col, i) => ({
      label: col,
      data: filteredRows.map((row) => table[row]?.[col] || 0),
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
    })),
  }), [filteredRows, cols, table]);

  if (data.length === 0) return null;

  return (
    <>
      <div className="mb-3">
        <ButtonGroup size="sm">
          <BsButton variant={timeMode === 'monthly' ? 'dark' : 'outline-dark'} onClick={() => setTimeMode('monthly')}>
            {t(prefix + 'monthly')}
          </BsButton>
          <BsButton variant={timeMode === 'yearly' ? 'dark' : 'outline-dark'} onClick={() => setTimeMode('yearly')}>
            {t(prefix + 'yearly')}
          </BsButton>
        </ButtonGroup>
      </div>
      <div style={{ height: '350px' }}>
        <Bar
          data={chartConfig}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true } },
          }}
        />
      </div>
    </>
  );
};

const ViewToggle = ({ viewMode, onChange }: { viewMode: ViewMode; onChange: (v: ViewMode) => void }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-3">
      <ButtonGroup size="sm">
        <BsButton variant={viewMode === 'table' ? 'dark' : 'outline-dark'} onClick={() => onChange('table')}>
          {t(prefix + 'viewTable')}
        </BsButton>
        <BsButton variant={viewMode === 'chart' ? 'dark' : 'outline-dark'} onClick={() => onChange('chart')}>
          {t(prefix + 'viewChart')}
        </BsButton>
      </ButtonGroup>
    </div>
  );
};

const DimensionSelectors = ({ rowKeys, rowKey, colKeys, colKey, onRowChange, onColChange, dimLabel }: {
  rowKeys: string[]; rowKey: string; colKeys: string[]; colKey: string;
  onRowChange: (v: string) => void; onColChange: (v: string) => void; dimLabel: (d: string) => string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
      {rowKeys.length > 1 && (
        <Form.Group style={{ minWidth: '150px' }}>
          <Form.Label className="mb-1 small fw-semibold">{t(prefix + 'rows')}</Form.Label>
          <Form.Select size="sm" value={rowKey} onChange={(e) => onRowChange(e.target.value)}>
            {rowKeys.map((k) => <option key={k} value={k}>{dimLabel(k)}</option>)}
          </Form.Select>
        </Form.Group>
      )}
      <Form.Group style={{ minWidth: '150px' }}>
        <Form.Label className="mb-1 small fw-semibold">{t(prefix + 'columns')}</Form.Label>
        <Form.Select size="sm" value={colKey} onChange={(e) => onColChange(e.target.value)}>
          {colKeys.map((k) => <option key={k} value={k}>{dimLabel(k)}</option>)}
        </Form.Select>
      </Form.Group>
    </div>
  );
};

// Override timePeriod label per metric
function metricDimLabel(baseDimLabel: (d: string) => string, t: (key: string) => string, metric: string) {
  return (dim: string) => {
    if (dim === 'timePeriod') {
      const key = prefix + 'dim_timePeriod_' + metric;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return baseDimLabel(dim);
  };
}

// --- Per-metric components (each calls its own hook at top level) ---

const playerRowOptions: Record<string, string[]> = {
  countryCode: ['gender', 'ageGroup', 'ratingBracketSingles', 'ratingBracketDoubles', 'timePeriod'],
  ageGroup: ['gender', 'countryCode', 'ratingBracketSingles', 'ratingBracketDoubles', 'timePeriod'],
  ratingBracketSingles: ['gender', 'countryCode', 'ageGroup', 'timePeriod'],
  ratingBracketDoubles: ['gender', 'countryCode', 'ageGroup', 'timePeriod'],
  timePeriod: ['gender', 'countryCode', 'ageGroup', 'ratingBracketSingles', 'ratingBracketDoubles'],
};

const PlayerSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'players');
  const [rowKey, setRowKey] = useState('countryCode');
  const [colKey, setColKey] = useState('gender');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : rowKey;
  const effectiveColKey = viewMode === 'chart' ? 'gender' : colKey;

  const { data, isLoading, isError } = useGetPlayerStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'countryCode' ? countryName(entry.row, i18n.language) : entry.row,
      column: effectiveColKey === 'countryCode' ? countryName(entry.column, i18n.language) : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  const handleRowChange = (newRow: string) => {
    setRowKey(newRow);
    if (!playerRowOptions[newRow].includes(colKey)) setColKey(playerRowOptions[newRow][0]);
  };

  return (
    <>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {viewMode === 'table' && (
        <DimensionSelectors
          rowKeys={Object.keys(playerRowOptions)} rowKey={rowKey}
          colKeys={playerRowOptions[rowKey]} colKey={colKey}
          onRowChange={handleRowChange} onColChange={setColKey} dimLabel={label}
        />
      )}
      {isLoading ? <Loading /> : isError ? <ErrorAlert prefix={prefix} /> : viewMode === 'chart' ? <StatChart data={translatedData} /> : <StatTable data={translatedData} rowLabel={label(rowKey)} />}
    </>
  );
};

const tournamentRowOptions: Record<string, string[]> = {
  country: ['status', 'timePeriod'],
  timePeriod: ['country', 'status'],
};

const TournamentSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'tournaments');
  const [rowKey, setRowKey] = useState('country');
  const [colKey, setColKey] = useState('status');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : rowKey;
  const effectiveColKey = viewMode === 'chart' ? 'country' : colKey;

  const { data, isLoading, isError } = useGetTournamentStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'country' ? countryName(entry.row, i18n.language) : entry.row,
      column: effectiveColKey === 'country' ? countryName(entry.column, i18n.language) : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  const handleRowChange = (newRow: string) => {
    setRowKey(newRow);
    if (!tournamentRowOptions[newRow].includes(colKey)) setColKey(tournamentRowOptions[newRow][0]);
  };

  if (isLoading) return <Loading />;
  if (isError) return <ErrorAlert prefix={prefix} />;

  return (
    <>
      <UpcomingTournaments />
      <h6 className="fw-bold mt-4 mb-3">{t(prefix + 'allTournaments')}</h6>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {viewMode === 'table' && (
        <DimensionSelectors
          rowKeys={Object.keys(tournamentRowOptions)} rowKey={rowKey}
          colKeys={tournamentRowOptions[rowKey]} colKey={colKey}
          onRowChange={handleRowChange} onColChange={setColKey} dimLabel={label}
        />
      )}
      {viewMode === 'chart' ? <StatChart data={translatedData} /> : <StatTable data={translatedData} rowLabel={label(rowKey)} />}
    </>
  );
};

const matchRowOptions: Record<string, string[]> = {
  country: ['status', 'matchType', 'duprStatus', 'timePeriod'],
  status: ['country', 'matchType', 'duprStatus', 'timePeriod'],
  matchType: ['country', 'status', 'duprStatus', 'timePeriod'],
  duprStatus: ['country', 'status', 'matchType', 'timePeriod'],
  timePeriod: ['country', 'status', 'matchType', 'duprStatus'],
};

const DuprMatchesChart = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetMatchStats({ rowKey: 'timePeriod' as any, columnKey: 'duprStatus' as any });

  if (isLoading) return <Loading />;
  if (isError) return <ErrorAlert prefix={prefix} />;
  if (!data || data.length === 0) return null;

  return (
    <div className="mt-4">
      <h6 className="fw-bold mb-3">{t(prefix + 'duprMatchesHeading')}</h6>
      <StatChart data={data} />
    </div>
  );
};

const MatchSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'matches');
  const [rowKey, setRowKey] = useState('country');
  const [colKey, setColKey] = useState('status');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : rowKey;
  const effectiveColKey = viewMode === 'chart' ? 'country' : colKey;

  const { data, isLoading, isError } = useGetMatchStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'country' ? countryName(entry.row, i18n.language) : entry.row,
      column: effectiveColKey === 'country' ? countryName(entry.column, i18n.language) : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  const handleRowChange = (newRow: string) => {
    setRowKey(newRow);
    if (!matchRowOptions[newRow].includes(colKey)) setColKey(matchRowOptions[newRow][0]);
  };

  return (
    <>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {viewMode === 'table' && (
        <DimensionSelectors
          rowKeys={Object.keys(matchRowOptions)} rowKey={rowKey}
          colKeys={matchRowOptions[rowKey]} colKey={colKey}
          onRowChange={handleRowChange} onColChange={setColKey} dimLabel={label}
        />
      )}
      {isLoading ? <Loading /> : isError ? <ErrorAlert prefix={prefix} /> : viewMode === 'chart' ? <StatChart data={translatedData} /> : <StatTable data={translatedData} rowLabel={label(rowKey)} />}
      {viewMode === 'chart' && <DuprMatchesChart />}
    </>
  );
};

const TourSection = ({ dimLabel }: { dimLabel: (d: string) => string }) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useGetTourStats();

  const tableData = useMemo(() =>
    (data?.playersByCountry || []).map((r) => ({
      row: countryName(r.countryCode, i18n.language),
      column: t(prefix + 'total'),
      count: r.count,
    })),
    [data, i18n.language, t],
  );

  if (isLoading) return <Loading />;
  if (isError) return <ErrorAlert prefix={prefix} />;
  return <StatTable data={tableData} rowLabel={dimLabel('countryCode')} />;
};

const CommunitiesSection = () => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useGetOrganizationDetails();

  const columns: ColumnDef<OrganizationDetailsResponseDto>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: t(prefix + 'communityName'),
      cell: ({ row }) => (
        <a
          href={`/community/${row.original.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.original.name}
        </a>
      ),
    },
    {
      accessorKey: 'countryCode',
      header: t(prefix + 'dim_country'),
      cell: (info) => countryName(info.getValue<string>(), i18n.language),
      sortingFn: (a, b) => countryName(a.original.countryCode, i18n.language).localeCompare(countryName(b.original.countryCode, i18n.language)),
    },
    {
      accessorKey: 'tournamentsCreated',
      header: t(prefix + 'tournamentsCreated'),
    },
    {
      id: 'lastTournamentDate',
      accessorFn: (row) => row.lastTournamentDate ? new Date(row.lastTournamentDate).getTime() : undefined,
      header: t(prefix + 'lastTournament'),
      cell: ({ row }) => row.original.lastTournamentDate
        ? new Date(row.original.lastTournamentDate).toLocaleDateString()
        : <span className="text-muted">{t(prefix + 'never')}</span>,
      sortUndefined: 'last',
    },
    {
      accessorKey: 'totalPlayersHosted',
      header: t(prefix + 'totalPlayersHosted'),
    },
  ], [t, i18n.language]);

  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastTournamentDate', desc: true }]);

  const tt = useReactTable({
    data: data || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) return <Loading />;
  if (isError) return <ErrorAlert prefix={prefix} />;
  if (!data || data.length === 0) return <Alert variant="info">{t(prefix + 'noData')}</Alert>;

  const cellPad = '10px 14px';
  const headerPad = '12px 14px';
  const numericIds = new Set(['tournamentsCreated', 'lastTournamentDate', 'totalPlayersHosted']);

  return (
    <div className="border rounded shadow-sm overflow-hidden">
      <Table hover responsive className="mb-0" style={{ fontSize: '0.9rem' }}>
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          {tt.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const numeric = numericIds.has(header.column.id);
                return (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    role="button"
                    tabIndex={0}
                    className={`fw-semibold ${numeric ? 'text-end' : ''}`}
                    style={{ cursor: 'pointer', userSelect: 'none', padding: headerPad, borderBottom: '1px solid #dee2e6' }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span style={{ opacity: sorted ? 1 : 0.3, marginLeft: 4 }}>
                      {sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '↕'}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {tt.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={numericIds.has(cell.column.id) ? 'text-end' : ''} style={{ padding: cellPad }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const classRowOptions: Record<string, string[]> = {
  country: ['classType', 'gender', 'format', 'timePeriod'],
  classType: ['country', 'gender', 'format', 'timePeriod'],
  timePeriod: ['country', 'classType', 'gender', 'format'],
};

const ClassSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'classes');
  const [rowKey, setRowKey] = useState('country');
  const [colKey, setColKey] = useState('classType');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : rowKey;
  const effectiveColKey = viewMode === 'chart' ? 'country' : colKey;

  const { data, isLoading, isError } = useGetClassStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'country' ? countryName(entry.row, i18n.language) : entry.row,
      column: effectiveColKey === 'country' ? countryName(entry.column, i18n.language) : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  const handleRowChange = (newRow: string) => {
    setRowKey(newRow);
    if (!classRowOptions[newRow].includes(colKey)) setColKey(classRowOptions[newRow][0]);
  };

  return (
    <>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {viewMode === 'table' && (
        <DimensionSelectors
          rowKeys={Object.keys(classRowOptions)} rowKey={rowKey}
          colKeys={classRowOptions[rowKey]} colKey={colKey}
          onRowChange={handleRowChange} onColChange={setColKey} dimLabel={label}
        />
      )}
      {isLoading ? <Loading /> : isError ? <ErrorAlert prefix={prefix} /> : viewMode === 'chart' ? <StatChart data={translatedData} /> : <StatTable data={translatedData} rowLabel={label(rowKey)} />}
    </>
  );
};

const ticketRowOptions: Record<string, string[]> = {
  country: ['status', 'currency', 'timePeriod'],
  status: ['country', 'currency', 'timePeriod'],
  currency: ['country', 'status', 'timePeriod'],
  timePeriod: ['country', 'status', 'currency'],
};

const TicketSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'tickets');
  const [rowKey, setRowKey] = useState('country');
  const [colKey, setColKey] = useState('status');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : rowKey;
  const effectiveColKey = viewMode === 'chart' ? 'country' : colKey;

  const { data, isLoading, isError } = useGetTicketStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'country' ? countryName(entry.row, i18n.language) : effectiveRowKey === 'currency' ? entry.row.toUpperCase() : entry.row,
      column: effectiveColKey === 'country' ? countryName(entry.column, i18n.language) : effectiveColKey === 'currency' ? entry.column.toUpperCase() : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  const handleRowChange = (newRow: string) => {
    setRowKey(newRow);
    if (!ticketRowOptions[newRow].includes(colKey)) setColKey(ticketRowOptions[newRow][0]);
  };

  return (
    <>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {viewMode === 'table' && (
        <DimensionSelectors
          rowKeys={Object.keys(ticketRowOptions)} rowKey={rowKey}
          colKeys={ticketRowOptions[rowKey]} colKey={colKey}
          onRowChange={handleRowChange} onColChange={setColKey} dimLabel={label}
        />
      )}
      {isLoading ? <Loading /> : isError ? <ErrorAlert prefix={prefix} /> : viewMode === 'chart' ? <StatChart data={translatedData as StatEntryResponseDto[]} /> : <StatTable data={translatedData as StatEntryResponseDto[]} rowLabel={label(rowKey)} />}
    </>
  );
};

const MessagesSection = ({ dimLabel, viewMode, onViewModeChange }: { dimLabel: (d: string) => string; viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void }) => {
  const { t, i18n } = useTranslation();
  const label = metricDimLabel(dimLabel, t, 'messages');

  const effectiveRowKey = viewMode === 'chart' ? 'timePeriod' : 'country';
  const effectiveColKey = viewMode === 'chart' ? 'country' : 'timePeriod';

  const { data, isLoading, isError } = useGetMessageStats({ rowKey: effectiveRowKey as any, columnKey: effectiveColKey as any });

  const translatedData = useMemo(() => {
    const entries = (data || []).map((entry) => ({
      ...entry,
      row: effectiveRowKey === 'country' ? countryName(entry.row, i18n.language) : entry.row,
      column: effectiveColKey === 'country' ? countryName(entry.column, i18n.language) : entry.column,
    }));
    if (viewMode === 'chart') {
      const totals: Record<string, number> = {};
      entries.forEach((e) => { totals[e.row] = (totals[e.row] || 0) + e.count; });
      return Object.entries(totals).map(([row, count]) => ({ row, column: t(prefix + 'total'), count }));
    }
    return entries;
  }, [data, effectiveRowKey, effectiveColKey, viewMode, i18n.language, t]);

  return (
    <>
      <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
      {isLoading ? <Loading /> : isError ? <ErrorAlert prefix={prefix} /> : viewMode === 'chart' ? <StatChart data={translatedData} /> : <StatTable data={translatedData} rowLabel={label(effectiveRowKey)} />}
    </>
  );
};

const UpcomingTournaments = () => {
  const { t, i18n } = useTranslation();
  const overviewQuery = useGetOverview();
  const tournaments = overviewQuery.data?.upcomingTournaments || [];

  const columns: ColumnDef<any>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: t(prefix + 'tournamentName'),
      cell: (info) => (
        <a
          href={`/community/${info.row.original.organizationId}/tournament/${info.row.original.id}/dashboard`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {info.getValue<string>()}
        </a>
      ),
    },
    {
      accessorKey: 'country',
      header: t(prefix + 'dim_country'),
      cell: (info) => countryName(info.getValue<string>(), i18n.language),
      sortingFn: (a, b) => countryName(a.original.country, i18n.language).localeCompare(countryName(b.original.country, i18n.language)),
    },
    {
      accessorKey: 'startDate',
      header: t(prefix + 'startDate'),
      cell: (info) => new Date(info.getValue<string>()).toLocaleDateString(),
    },
    {
      accessorKey: 'registeredPlayers',
      header: t(prefix + 'registeredPlayers'),
    },
  ], [t, i18n.language]);

  const [sorting, setSorting] = useState<SortingState>([{ id: 'startDate', desc: false }]);
  const [expanded, setExpanded] = useState(false);

  const tt = useReactTable({
    data: tournaments,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (overviewQuery.isLoading) return <Loading />;
  if (overviewQuery.isError) return <ErrorAlert prefix={prefix} />;
  if (tournaments.length === 0) return <Alert variant="info">{t(prefix + 'noUpcoming')}</Alert>;

  const cellPad = '10px 14px';
  const headerPad = '12px 14px';
  const numericIds = new Set(['startDate', 'registeredPlayers']);

  return (
    <>
      <h6
        className="fw-bold mb-3 d-inline-flex align-items-center gap-2"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        {t(prefix + 'upcomingHeading', { count: tournaments.length })}
      </h6>
      {expanded && (
      <div className="border rounded shadow-sm overflow-hidden">
        <Table hover responsive className="mb-0" style={{ fontSize: '0.9rem' }}>
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            {tt.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const numeric = numericIds.has(header.column.id);
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      role="button"
                      tabIndex={0}
                      className={`fw-semibold ${numeric ? 'text-end' : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none', padding: headerPad, borderBottom: '1px solid #dee2e6' }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span style={{ opacity: sorted ? 1 : 0.3, marginLeft: 4 }}>
                        {sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '↕'}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {tt.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={numericIds.has(cell.column.id) ? 'text-end' : ''} style={{ padding: cellPad }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      )}
    </>
  );
};

// --- Main component ---

export const AdminStatistics = () => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<Metric>('tournaments');
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const overviewQuery = useGetOverview();
  const overview = overviewQuery.data;

  const dimLabel = (dim: string) => t(prefix + 'dim_' + dim);

  return (
    <div>
      {/* Metric Cards */}
      {overviewQuery.isLoading ? (
        <Loading />
      ) : overviewQuery.isError ? (
        <ErrorAlert prefix={prefix} />
      ) : overview && (
        <div className="d-flex flex-wrap gap-3 mb-4">
          <StatCard label={t(prefix + 'totalTournaments')} value={overview.totalTournaments} subLabel={`${overview.upcomingTournaments.length} ${t(prefix + 'upcomingThisWeek')}`} active={metric === 'tournaments'} onClick={() => setMetric('tournaments')} />
          <StatCard label={t(prefix + 'totalPlayers')} value={overview.totalPlayers} subLabel={`+${overview.newPlayersLast24h} ${t(prefix + 'last24h')}`} active={metric === 'players'} onClick={() => setMetric('players')} />
          <StatCard label={t(prefix + 'totalTickets')} value={overview.totalTicketsSold} subLabel={`+${overview.ticketsSoldLast24h} ${t(prefix + 'last24h')}`} active={metric === 'tickets'} onClick={() => setMetric('tickets')} />
          <StatCard label={t(prefix + 'totalMessages')} value={overview.totalMessages} subLabel={`+${overview.messagesToday} ${t(prefix + 'today')}`} active={metric === 'messages'} onClick={() => setMetric('messages')} />
          <StatCard label={t(prefix + 'totalMatches')} value={overview.totalMatches} active={metric === 'matches'} onClick={() => setMetric('matches')} />
          <StatCard label={t(prefix + 'totalTours')} value={overview.totalTours} active={metric === 'tours'} onClick={() => setMetric('tours')} />
          <StatCard label={t(prefix + 'totalOrganizations')} value={overview.totalOrganizations} active={metric === 'organizations'} onClick={() => setMetric('organizations')} />
          <StatCard label={t(prefix + 'totalClasses')} value={overview.totalClasses} active={metric === 'classes'} onClick={() => setMetric('classes')} />
        </div>
      )}

      {metric === 'players' && <PlayerSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
      {metric === 'tournaments' && <TournamentSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
      {metric === 'tours' && <TourSection dimLabel={dimLabel} />}
      {metric === 'matches' && <MatchSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
      {metric === 'organizations' && <CommunitiesSection />}
      {metric === 'classes' && <ClassSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
      {metric === 'tickets' && <TicketSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
      {metric === 'messages' && <MessagesSection dimLabel={dimLabel} viewMode={viewMode} onViewModeChange={setViewMode} />}
    </div>
  );
};
